/**
 * Страница списка сотрудников компании с назначением роли миниаппа.
 * Редактирование роли доступно только администратору компании (isCompanyAdmin в сессии).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import {
  Avatar,
  Button,
  DataTable,
  Paper,
  PaginationControlled,
  CollapsibleListFilters,
  FilterFieldLabel,
  FiltersPanel,
  FilterTextInput,
} from '../uikit';
import type { IDataTableColumn, ISortedTable, OrderDirectionType } from '../uikit';
import type { DisplayMiniappRole, Employee } from '../api/employees';
import { findEmployees, ROLE_LABELS } from '../api/employees';
import { toUserMessage } from '../api/client';
import { useAuth } from '../auth';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { EmployeeRowRoleDropdown } from '../components/EmployeeRowRoleDropdown';
import { formatFullName } from '../utils/formatFullName';
import { getConfig } from '../config';

const RolesBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space[0]}px;
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 2px ${({ theme }) => theme.space[1]}px;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background-color: ${({ theme }) => theme.colors.highlight.gray25};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  ${({ theme }) => theme.device.mobile} {
    align-items: flex-start;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin: 0 -${({ theme }) => theme.space[2]}px 12px;
  padding-left: ${({ theme }) => theme.space[2]}px;
  padding-right: ${({ theme }) => theme.space[2]}px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const PageTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  line-height: 1.5;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ErrorRow = styled.div`
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  ${({ theme }) => theme.device.mobile} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const PageLayout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const TableSection = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const AssignErrorRow = styled.div`
  margin-bottom: 12px;
  padding: ${({ theme }) => theme.space[2]}px;
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.error.light};
  color: ${({ theme }) => theme.colors.error.dark};
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

function formatRoleLabel(miniappRole: string | undefined): string {
  if (!miniappRole) return '—';
  return ROLE_LABELS[miniappRole as DisplayMiniappRole] ?? miniappRole;
}

function getEmployeeColumns(
  canManageRoles: boolean,
  onRoleAssigned: () => void,
  onRoleAssignError: (message: string) => void,
): IDataTableColumn<Employee>[] {
  const istockURL = getConfig().istockURL;

  return [
    {
      key: 'name',
      header: 'ФИО',
      sortable: true,
      fixedWidth: 280,
      cellStyle: { px: 2 },
      render: (_, item) => {
        const fullName =
          formatFullName(item.familyName, item.givenName, item.patronymic) || '—';
        return (
          <NameCell>
            <Avatar
              url={item.avatarUrl}
              letter={fullName}
              size="xs"
              alt={fullName}
            />
            <div>
              <div>{fullName}</div>
            </div>
          </NameCell>
        );
      },
    },
    {
      key: 'email',
      header: 'Email',
      sortable: false,
      fixedWidth: 220,
      cellStyle: { px: 2 },
      render: (value) => (value ? String(value) : '—'),
    },
    {
      key: 'miniappRole',
      header: 'Роль',
      sortable: false,
      fixedWidth: 220,
      cellStyle: { px: 2 },
      render: (_, item) => {
        const hasMiniapp = Boolean(item.miniappRole);
        const showAdmin = item.isCompanyAdmin === true;
        if (!showAdmin && !hasMiniapp) return '—';
        const miniappRole = item.miniappRole ?? '';
        const hideMiniappBadgeWhenSameAsCompanyAdmin = showAdmin && miniappRole === 'administrator';
        return (
          <RolesBadges>
            {showAdmin && (
              <RoleBadge key="company-admin">Администратор компании</RoleBadge>
            )}
            {hasMiniapp && !hideMiniappBadgeWhenSameAsCompanyAdmin && (
              <RoleBadge key="miniapp">{formatRoleLabel(item.miniappRole)}</RoleBadge>
            )}
          </RolesBadges>
        );
      },
    },
    ...(canManageRoles
      ? [
          {
            key: 'rowActions',
            header: { text: '' },
            sortable: false,
            fixedWidth: 86,
            truncate: false,
            cellStyle: { px: 1, textAlign: 'right' },
            render: (_: unknown, item: Employee) => {
              if (item.isCompanyAdmin === true) return null;
              return (
                <EmployeeRowRoleDropdown
                  employee={item}
                  editUrl={`${istockURL}/company/employees/${item.id}`}
                  onAssigned={onRoleAssigned}
                  onAssignError={onRoleAssignError}
                />
              );
            },
          } as IDataTableColumn<Employee>,
        ]
      : []),
  ];
}

export function EmployeesListPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const canManageRoles = user?.isCompanyAdmin === true;

  const [items, setItems] = useState<Employee[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [assignRoleError, setAssignRoleError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, 300);
  const [sort, setSort] = useState<ISortedTable>({ orderBy: 'name', orderDirection: 'ASC' });

  const loadEmployees = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const result = await findEmployees({
        search: debouncedSearch,
        page,
        itemsPerPage,
      });
      setItems(result.items);
      setCount(result.count);
    } catch (err) {
      setItems([]);
      setCount(0);
      setLoadError(toUserMessage(err));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page, itemsPerPage]);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const handleRoleAssigned = useCallback(() => {
    setAssignRoleError(null);
    void loadEmployees();
  }, [loadEmployees]);

  const handleSort = (key: string, direction: OrderDirectionType) => {
    setSort({ orderBy: key, orderDirection: direction });
    setPage(1);
  };

  const getEmployeeDisplayName = (e: Employee) =>
    formatFullName(e.familyName, e.givenName, e.patronymic) || '';

  const sortedItems = useMemo(() => {
    if (sort.orderBy !== 'name') return items;
    const sorted = [...items].sort((a, b) => {
      const nameA = getEmployeeDisplayName(a).toLowerCase();
      const nameB = getEmployeeDisplayName(b).toLowerCase();
      const cmp = nameA.localeCompare(nameB);
      return sort.orderDirection === 'ASC' ? cmp : -cmp;
    });
    return sorted;
  }, [items, sort.orderBy, sort.orderDirection]);

  const columns = getEmployeeColumns(canManageRoles, handleRoleAssigned, setAssignRoleError);

  return (
    <>
      <Paper padding={16} shadow={0} style={{ marginBottom: 0, height: '100%' }}>
        <PageLayout>
          <HeaderRow>
            <PageTitle>Сотрудники</PageTitle>
          </HeaderRow>
          <CollapsibleListFilters activeFilterCount={searchInput.trim() !== '' ? 1 : 0}>
            <FiltersPanel $borderless>
            <FilterFieldLabel htmlFor="employees-search-filter">
              Поиск:
              <FilterTextInput
                id="employees-search-filter"
                type="search"
                placeholder="Поиск по ФИО, email..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                enterKeyHint="search"
                aria-label="Поиск сотрудников"
                $maxWidthPx={280}
              />
            </FilterFieldLabel>
            </FiltersPanel>
          </CollapsibleListFilters>
          {assignRoleError && (
            <AssignErrorRow role="alert">
              <span>{assignRoleError}</span>
              <Button variant="text" color="primary" size="s" onClick={() => setAssignRoleError(null)}>
                Скрыть
              </Button>
            </AssignErrorRow>
          )}
          <TableSection>
            <DataTable
              data={sortedItems}
              columns={columns}
              orderBy={sort.orderBy}
              orderDirection={sort.orderDirection}
              onSort={handleSort}
              loading={loading}
              settings={{ horizontalLine: true, backlightLine: true }}
              minHeight={0}
              height="100%"
              placeholder={{ text: 'Сотрудники не найдены' }}
            />
          </TableSection>
          {loadError && (
            <ErrorRow role="alert">
              <span style={{ color: theme.colors.error.main, fontSize: theme.fontSizes[5] }}>{loadError}</span>
              <Button variant="outlined" color="primary" size="s" onClick={() => void loadEmployees()}>
                Повторить
              </Button>
            </ErrorRow>
          )}
          <PaginationControlled
            page={page}
            itemsPerPage={itemsPerPage}
            totalItems={count}
            setPage={setPage}
            setItemsPerPage={setItemsPerPage}
          />
        </PageLayout>
      </Paper>
    </>
  );
}
